/**
 * Link component for SPA navigation
 * Intercepts click events and uses the router for navigation instead of full page reload
 */

import { Router } from '../services/router';

/**
 * Link options for creating anchor elements
 */
export interface LinkOptions {
  /**
   * The path to navigate to
   */
  href: string;
  /**
   * The text or HTML content of the link
   */
  content: string | HTMLElement;
  /**
   * Optional CSS class name(s)
   */
  className?: string;
  /**
   * Optional additional attributes
   */
  attributes?: Record<string, string>;
  /**
   * Whether to replace the current history entry instead of pushing
   */
  replace?: boolean;
  /**
   * Optional callback before navigation
   */
  onBeforeNavigate?: (href: string) => boolean;
}

/**
 * Global router instance reference
 */
let globalRouter: Router | null = null;

/**
 * Set the global router instance for Link components
 */
export function setLinkRouter(router: Router): void {
  globalRouter = router;
}

/**
 * Get the global router instance
 */
export function getLinkRouter(): Router | null {
  return globalRouter;
}

/**
 * Create a Link element for SPA navigation
 */
export function createLink(options: LinkOptions): HTMLAnchorElement {
  const { href, content, className, attributes, replace = false, onBeforeNavigate } = options;

  const link = document.createElement('a');
  link.href = href;

  // Set content
  if (typeof content === 'string') {
    link.innerHTML = content;
  } else {
    link.appendChild(content);
  }

  // Set class
  if (className) {
    link.className = className;
  }

  // Set additional attributes
  if (attributes) {
    for (const [key, value] of Object.entries(attributes)) {
      link.setAttribute(key, value);
    }
  }

  // Handle click for SPA navigation
  link.addEventListener('click', (event) => {
    // Allow default behavior for:
    // - Modified clicks (ctrl, meta, shift, alt)
    // - Middle/right mouse buttons
    // - External links (different origin)
    // - Links with target attribute
    // - Links with download attribute
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0 ||
      link.target ||
      link.hasAttribute('download')
    ) {
      return;
    }

    // Check if external link
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) {
        return; // Let browser handle external links
      }
    } catch {
      return; // Invalid URL, let browser handle
    }

    // Call before navigate callback
    if (onBeforeNavigate && !onBeforeNavigate(href)) {
      event.preventDefault();
      return;
    }

    // Prevent default navigation
    event.preventDefault();

    // Use router for navigation
    if (globalRouter) {
      globalRouter.navigate(href, replace);
    } else {
      console.warn('Link: No router configured. Call setLinkRouter() first.');
      // Fallback to standard navigation
      window.location.href = href;
    }
  });

  return link;
}

/**
 * Link class for object-oriented usage
 */
export class Link {
  private element: HTMLAnchorElement;
  private options: LinkOptions;

  constructor(options: LinkOptions) {
    this.options = options;
    this.element = createLink(options);
  }

  /**
   * Get the anchor element
   */
  getElement(): HTMLAnchorElement {
    return this.element;
  }

  /**
   * Update the href
   */
  setHref(href: string): this {
    this.options.href = href;
    this.element.href = href;
    return this;
  }

  /**
   * Update the content
   */
  setContent(content: string | HTMLElement): this {
    this.element.innerHTML = '';
    if (typeof content === 'string') {
      this.element.innerHTML = content;
    } else {
      this.element.appendChild(content);
    }
    return this;
  }

  /**
   * Set CSS classes
   */
  setClassName(className: string): this {
    this.element.className = className;
    return this;
  }

  /**
   * Add CSS class
   */
  addClass(className: string): this {
    this.element.classList.add(className);
    return this;
  }

  /**
   * Remove CSS class
   */
  removeClass(className: string): this {
    this.element.classList.remove(className);
    return this;
  }

  /**
   * Check if link is active (matches current path)
   */
  isActive(exactMatch = false): boolean {
    const currentPath = window.location.pathname;
    const linkPath = new URL(this.options.href, window.location.origin).pathname;

    if (exactMatch) {
      return currentPath === linkPath;
    }
    return currentPath.startsWith(linkPath);
  }

  /**
   * Update active state CSS class
   */
  updateActiveState(activeClass = 'active', exactMatch = false): this {
    if (this.isActive(exactMatch)) {
      this.element.classList.add(activeClass);
    } else {
      this.element.classList.remove(activeClass);
    }
    return this;
  }
}

/**
 * Install link click handler on document to intercept all anchor clicks
 * This is an alternative to creating Link components for every anchor
 */
export function installLinkInterceptor(options?: {
  /**
   * Selector for links to intercept (default: 'a[data-spa-link]')
   */
  selector?: string;
  /**
   * Whether to intercept all internal links (default: false)
   */
  interceptAll?: boolean;
}): () => void {
  const { selector = 'a[data-spa-link]', interceptAll = false } = options || {};

  const handler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a') as HTMLAnchorElement | null;

    if (!link) return;

    // Check if we should intercept this link
    const shouldIntercept = interceptAll
      ? !link.hasAttribute('data-spa-external')
      : link.matches(selector);

    if (!shouldIntercept) return;

    // Allow default behavior for modified clicks
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0 ||
      link.target ||
      link.hasAttribute('download')
    ) {
      return;
    }

    // Check if external link
    try {
      const url = new URL(link.href);
      if (url.origin !== window.location.origin) {
        return;
      }
    } catch {
      return;
    }

    event.preventDefault();

    if (globalRouter) {
      const replace = link.hasAttribute('data-spa-replace');
      globalRouter.navigate(link.pathname + link.search + link.hash, replace);
    } else {
      console.warn('Link interceptor: No router configured.');
    }
  };

  document.addEventListener('click', handler);

  // Return cleanup function
  return () => {
    document.removeEventListener('click', handler);
  };
}
